/**
 * Contacts-style ordering for the student roster.
 *
 * A teaching roster is a small, personal list — instructors think of
 * students by first name — so we order by first name (falling back to the
 * last name when there's no first name), case- and accent-insensitive,
 * then group under an A–Z section letter with a trailing "#" bucket for
 * names that don't start with a letter. Sorting by first name also keeps
 * each row's avatar initial in step with its section header. The mobile
 * app carries a byte-for-byte twin at `mobile/src/lib/studentSort.js`
 * — keep the two in sync.
 */

/** Full jump-index alphabet, matching the iOS Contacts rail (A–Z then #). */
export const ALPHABET = [
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
  '#',
];

/**
 * The name a student sorts under: first name when present, otherwise the
 * last name (so a first-name-only "Alex" still files under "Alex" and a
 * record with only a surname still sorts sensibly). Trimmed; '' when the
 * student has no name.
 * @param {{ first_name?: string, last_name?: string } | null | undefined} s
 * @returns {string}
 */
function primarySortName(s) {
  const first = (s?.first_name || '').trim();
  if (first) return first;
  return (s?.last_name || '').trim();
}

/**
 * The A–Z section a student belongs to — the uppercased first letter of
 * its primary sort name, or '#' when that isn't A–Z (digits, symbols,
 * emoji, or an empty name).
 * @param {{ first_name?: string, last_name?: string } | null | undefined} s
 * @returns {string} 'A'–'Z' or '#'
 */
export function studentSectionLetter(s) {
  const c = primarySortName(s).charAt(0).toUpperCase();
  return c >= 'A' && c <= 'Z' ? c : '#';
}

/**
 * Comparator ordering two students like phone contacts: by first name
 * (last-name fallback), then last name, then email as a final tiebreaker.
 * Locale-aware and case/accent-insensitive.
 * @param {Object} a
 * @param {Object} b
 * @returns {number}
 */
export function compareStudents(a, b) {
  const opts = { sensitivity: 'base', numeric: true };
  const byPrimary = primarySortName(a).localeCompare(primarySortName(b), undefined, opts);
  if (byPrimary !== 0) return byPrimary;
  const byLast = (a?.last_name || '').trim().localeCompare((b?.last_name || '').trim(), undefined, opts);
  if (byLast !== 0) return byLast;
  return (a?.email || '').localeCompare(b?.email || '', undefined, { sensitivity: 'base' });
}

/**
 * A new array of students in contacts order. Does not mutate the input.
 * @param {Array<Object>} list
 * @returns {Array<Object>}
 */
export function sortStudents(list) {
  return [...(list || [])].sort(compareStudents);
}

/**
 * Group students into `SectionList`-ready sections keyed by their A–Z
 * letter, sorted within and across sections. The '#' bucket always sorts
 * last, matching iOS Contacts.
 * @param {Array<Object>} list
 * @returns {Array<{ letter: string, title: string, data: Array<Object> }>}
 */
export function groupStudentsIntoSections(list) {
  const byLetter = new Map();
  for (const student of sortStudents(list)) {
    const letter = studentSectionLetter(student);
    if (!byLetter.has(letter)) byLetter.set(letter, []);
    byLetter.get(letter).push(student);
  }
  const sections = [];
  for (const letter of ALPHABET) {
    if (byLetter.has(letter)) {
      sections.push({ letter, title: letter, data: byLetter.get(letter) });
    }
  }
  return sections;
}
